import _uniqBy from 'lodash/uniqBy';

/**
 * Adjusts the contrast of the given image data.
 *
 * @param {ImageData} imageData
 * @param {number} contrastAdjustment Contrast adjustment (input range [-100..100])
 */
export function adjustContrast(imageData, contrastAdjustment) {
  const { data } = imageData;

  const contrast = contrastAdjustment / 100 + 1; // convert to decimal & shift range: [0..2]
  const intercept = 128 * (1 - contrast);

  for (let i = 0; i < data.length; i += 4) {
    // r,g,b,a
    data[i] = data[i] * contrast + intercept;
    data[i + 1] = data[i + 1] * contrast + intercept;
    data[i + 2] = data[i + 2] * contrast + intercept;
  }

  return imageData;
}

/**
 * Scans the given image data for text.
 *
 * @param {import('tesseract.js').Worker} ocrWorker
 * @param {ImageData} imageData
 */
export async function ocr(ocrWorker, imageData) {
  // Greyscale
  const greyImageData = greyscale(imageData);
  const greyscaleMatches = await recognize(ocrWorker, greyImageData);

  // Threshold
  const thresholdImageData = threshold(greyImageData);
  const thresholdMatches = await recognize(ocrWorker, thresholdImageData);

  // Reverse and threshold
  const invertedThresholdImageData = threshold(invert(greyImageData));
  const invertedThresholdMatches = await recognize(ocrWorker, invertedThresholdImageData);

  // TODO: figure out overlapping bbox results and find the best one
  return _uniqBy([...greyscaleMatches, ...thresholdMatches, ...invertedThresholdMatches], 'text');
}

/**
 * Converts the given image data into greyscale image data.
 *
 * @param {ImageData} imageData
 */
export function greyscale(imageData) {
  const rgbaData = [...imageData.data]; // [r,g,b,a,...]

  for (let i = 0; i < rgbaData.length; i += 4) {
    const avg = (rgbaData[i] + rgbaData[i + 1] + rgbaData[i + 2]) / 3;
    rgbaData[i] = avg; // red
    rgbaData[i + 1] = avg; // green
    rgbaData[i + 2] = avg; // blue
  }

  return new ImageData(new Uint8ClampedArray(rgbaData), imageData.width, imageData.height);
}

/**
 * Converts the given image data into greyscale image data.
 *
 * @param {ImageData} imageData
 */
export function invert(imageData) {
  const rgbaData = [...imageData.data]; // [r,g,b,a,...]

  for (let i = 0; i < rgbaData.length; i += 4) {
    rgbaData[i] = 255 - rgbaData[i]; // red
    rgbaData[i + 1] = 255 - rgbaData[i + 1]; // green
    rgbaData[i + 2] = 255 - rgbaData[i + 2]; // blue
  }

  return new ImageData(new Uint8ClampedArray(rgbaData), imageData.width, imageData.height);
}

/**
 * Converts the given image data colors into black/white based on the given threshold.
 *
 * @param {ImageData} imageData
 * @param {number} threshold
 */
export function threshold(imageData, thresholdValue = 128) {
  const rgbaData = [...imageData.data]; // [r,g,b,a,...]
  for (let i = 0; i < rgbaData.length; i += 4) {
    // R=G=B=R>T?255:0
    rgbaData[i] = rgbaData[i + 1] = rgbaData[i + 2] = rgbaData[i + 1] > thresholdValue ? 255 : 0;
  }
  return new ImageData(new Uint8ClampedArray(rgbaData), imageData.width, imageData.height);
}

/**
 * Attempts to recognize text of the given ImageData.
 *
 * @param {import('tesseract.js').Worker} ocrWorker
 * @param {ImageData} imageData
 */
export async function recognize(ocrWorker, imageData) {
  const { height, width } = imageData;
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  ctx.putImageData(imageData, 0, 0);

  const imageBlob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg'));

  const result = await ocrWorker.recognize(imageBlob);

  /** @type {{ bbox: import('tesseract.js').Bbox, confidence: number, text: string }[]} */
  const found = [];

  result.data.lines.forEach((line) => {
    const { bbox, confidence } = line;
    const text = line.text.trim();
    if (confidence > 75 || text.length >= 17) {
      found.push({
        bbox,
        confidence,
        text,
      });
    }
  });

  return found;
}
